import { useEffect, useMemo } from "react";
import { applyPageMeta, restorePageMeta, setPageJsonLd, clearPageJsonLd, canonicalUrl, DEFAULT_OG_IMAGE } from "@/lib/seo";

export interface PageMetaProps {
  /** Full document title, used verbatim (e.g. "FAQ | Stasht"). */
  title: string;
  description: string;
  /** Site-relative canonical path for this page (e.g. "/faq"). */
  path: string;
  ogType?: "website" | "article";
  /** Absolute URL of the social share image. Defaults to the site-wide OG image. */
  ogImage?: string;
  /** Accessible description of the social image. Defaults to the site-wide card description. */
  ogImageAlt?: string;
  /**
   * JSON-LD blocks for this page. Define these as module-level constants so
   * the reference stays stable across renders.
   */
  jsonLd?: ReadonlyArray<Record<string, unknown>>;
}

/**
 * Declarative per-route metadata. Renders nothing; mutates the document head
 * in place (no duplicate tags) and restores the index.html defaults when the
 * page unmounts. Mount exactly one per route.
 */
export const PageMeta = ({ title, description, path, ogType = "website", ogImage, ogImageAlt, jsonLd }: PageMetaProps) => {
  // Serialize for the effect dependency so inline arrays don't re-trigger it.
  const jsonLdKey = useMemo(() => JSON.stringify(jsonLd ?? []), [jsonLd]);

  useEffect(() => {
    applyPageMeta({
      title,
      description,
      canonical: canonicalUrl(path),
      ogType,
      ogImage: ogImage ?? DEFAULT_OG_IMAGE,
      ogImageAlt: ogImageAlt ?? "Stasht — Saves That Work",
    });
    setPageJsonLd(JSON.parse(jsonLdKey));

    return () => {
      restorePageMeta();
      clearPageJsonLd();
    };
  }, [title, description, path, ogType, ogImage, ogImageAlt, jsonLdKey]);

  return null;
};
